import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseTypewriterOptions {
  enabled?: boolean;
  charDelay?: number;
  startDelay?: number;
}

export function useTypewriter(
  content: string,
  options: UseTypewriterOptions = {},
) {
  const { enabled = true, charDelay = 40, startDelay = 0 } = options;

  const [output, setOutput] = useState(content);
  const [isDone, setIsDone] = useState(true);
  const reduceMotion = usePrefersReducedMotion();

  const startTimeoutRef = useRef<number | undefined>(undefined);
  const intervalRef = useRef<number | undefined>(undefined);
  const indexRef = useRef(0);

  const clearTimers = useCallback(() => {
    if (startTimeoutRef.current !== undefined) {
      window.clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = undefined;
    }
    if (intervalRef.current !== undefined) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }, []);

  const finish = useCallback(() => {
    clearTimers();
    indexRef.current = content.length;
    setOutput(content);
    setIsDone(true);
  }, [clearTimers, content]);

  const shouldAnimate = useMemo(() => {
    if (!enabled || !content.length || reduceMotion) {
      return false;
    }
    return true;
  }, [content.length, enabled, reduceMotion]);

  useEffect(() => {
    if (!shouldAnimate) {
      finish();
      return;
    }

    indexRef.current = 0;
    setIsDone(false);
    setOutput("");

    startTimeoutRef.current = window.setTimeout(
      () => {
        intervalRef.current = window.setInterval(
          () => {
            indexRef.current += 1;
            setOutput(content.slice(0, indexRef.current));

            if (indexRef.current >= content.length) {
              finish();
            }
          },
          Math.max(charDelay, 16),
        );
      },
      Math.max(startDelay, 0),
    );

    return finish;
  }, [charDelay, content, finish, shouldAnimate, startDelay]);

  return {
    text: output,
    isDone,
    skip: finish,
  };
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    setPrefersReducedMotion(mediaQuery.matches);

    try {
      mediaQuery.addEventListener("change", updatePreference);
      return () => mediaQuery.removeEventListener("change", updatePreference);
    } catch {
      mediaQuery.addListener(updatePreference);
      return () => mediaQuery.removeListener(updatePreference);
    }
  }, []);

  return prefersReducedMotion;
}
